import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, Search, CheckCircle, XCircle, Filter, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHANNELS = ['api', 'twilio', 'dotnet_sms', 'stripe', 'web', 'admin', 'llm', 'direct_sms'];
const EVENT_TYPES = [
  'central_dispatch', 'central_dispatch_update', 'central_dispatch_cancel',
  'dispatch_sms', 'dispatch_sms_update', 'dispatch_sms_cancel',
  'deposit_paid', 'quote_created', 'load_status_update',
  'pickup_confirmation_sent', 'delivery_confirmation_sent',
  'admin_login', 'admin_register',
];

export default function AuditLogsPage() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // 'success' | 'failed' | ''

  const fetchLogs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/audit-logs?limit=200`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setLogs(await response.json());
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const filteredLogs = logs.filter(log => {
    if (search && !log.event_type?.toLowerCase().includes(search.toLowerCase()) &&
        !log.message?.toLowerCase().includes(search.toLowerCase()) &&
        !log.channel?.toLowerCase().includes(search.toLowerCase()) &&
        !log.load_id?.toLowerCase().includes(search.toLowerCase())) return false;
    if (channelFilter && log.channel !== channelFilter) return false;
    if (eventFilter && log.event_type !== eventFilter) return false;
    if (statusFilter === 'success' && !log.success) return false;
    if (statusFilter === 'failed' && log.success !== false) return false;
    return true;
  });

  const hasFilters = search || channelFilter || eventFilter || statusFilter;
  const clearFilters = () => { setSearch(''); setChannelFilter(''); setEventFilter(''); setStatusFilter(''); };

  const channelColors = {
    web: 'bg-blue-500/10 text-blue-500',
    twilio: 'bg-purple-500/10 text-purple-500',
    stripe: 'bg-green-500/10 text-green-500',
    admin: 'bg-orange-500/10 text-orange-500',
    llm: 'bg-cyan-500/10 text-cyan-500',
    api: 'bg-yellow-500/10 text-yellow-500',
    direct_sms: 'bg-purple-500/10 text-purple-400',
  };

  const FilterPill = ({ label, value, active, onClick }) => (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
        active
          ? 'bg-accent text-accent-foreground border-accent'
          : 'bg-transparent text-muted-foreground border-border hover:border-white/30'
      }`}
    >
      {label}
    </button>
  );

  return (
      <div className="space-y-4" data-testid="admin-audit-logs">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-outfit text-2xl font-semibold">Audit Logs</h1>
            <p className="text-muted-foreground text-sm">Track all system events and actions</p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search logs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-transparent border-border"
              data-testid="audit-log-search"
            />
          </div>
        </div>

        {/* Filter bar */}
        <div className="rounded-xl border border-white/5 bg-card p-4 space-y-3">
          {/* Channel filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground w-16 shrink-0 flex items-center gap-1">
              <Filter className="h-3 w-3" /> Channel
            </span>
            <FilterPill label="All" value="" active={!channelFilter} onClick={() => setChannelFilter('')} />
            {CHANNELS.map(ch => (
              <FilterPill key={ch} label={ch} value={ch} active={channelFilter === ch} onClick={() => setChannelFilter(ch === channelFilter ? '' : ch)} />
            ))}
          </div>

          {/* Event type filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground w-16 shrink-0 flex items-center gap-1">
              <Filter className="h-3 w-3" /> Event
            </span>
            <FilterPill label="All" value="" active={!eventFilter} onClick={() => setEventFilter('')} />
            {EVENT_TYPES.map(ev => (
              <FilterPill key={ev} label={ev} value={ev} active={eventFilter === ev} onClick={() => setEventFilter(ev === eventFilter ? '' : ev)} />
            ))}
          </div>

          {/* Status filter */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-muted-foreground w-16 shrink-0 flex items-center gap-1">
              <Filter className="h-3 w-3" /> Status
            </span>
            <FilterPill label="All" active={!statusFilter} onClick={() => setStatusFilter('')} />
            <FilterPill label=" Success" active={statusFilter === 'success'} onClick={() => setStatusFilter(statusFilter === 'success' ? '' : 'success')} />
            <FilterPill label="x Failed" active={statusFilter === 'failed'} onClick={() => setStatusFilter(statusFilter === 'failed' ? '' : 'failed')} />

            {hasFilters && (
              <button onClick={clearFilters} className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                <X className="h-3 w-3" /> Clear all
              </button>
            )}
          </div>
        </div>

        {/* Results count */}
        <p className="text-xs text-muted-foreground px-1">
          {filteredLogs.length} of {logs.length} log{logs.length !== 1 ? 's' : ''}
          {hasFilters && ' (filtered)'}
        </p>

        {/* Log list */}
        <Card className="bg-card border-white/5">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-20 text-muted-foreground">Loading...</div>
            ) : filteredLogs.length === 0 ? (
              <div className="text-center py-20">
                <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No audit logs match the current filters</p>
                {hasFilters && (
                  <button onClick={clearFilters} className="mt-2 text-xs text-accent hover:underline">Clear filters</button>
                )}
              </div>
            ) : (
              <ScrollArea className="h-[600px]">
                <div className="divide-y divide-border">
                  {filteredLogs.map((log) => (
                    <div key={log.id} className="p-4 hover:bg-muted/20 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          {log.success !== false ? (
                            <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm">{log.event_type}</span>
                              <Badge className={channelColors[log.channel] || 'bg-muted'} variant="outline">
                                {log.channel}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1 break-words">{log.message}</p>
                            {log.load_id && (
                              <p className="text-xs text-muted-foreground mt-1 font-mono">
                                Load: {log.load_id.slice(0, 8)}...
                              </p>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(log.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
  );
}


